import type { EppoData } from '$lib/storyblok/types';
import { getAssignment } from '$lib/utils/eppo';
import type { MutatorCache, PromiseCache } from '../api';
import type { SbContentMutator } from '../mutationHelpers';

export function eppoFieldMutator(
	serverFetch: typeof fetch,
	url: URL,
	cache: MutatorCache['eppo'],
	promiseCache: PromiseCache['eppo']
): SbContentMutator<EppoData> {
	return {
		check: (_, content) => content.component === 'eppo_variant',
		mutate: async (_, content) => {
			if (!content.eppo?.feature_flag) return;

			const flagKey = content.eppo.feature_flag.key;

			if (cache[flagKey]) {
				content.eppo.assignment = cache[flagKey];
				return;
			}

			const existingPromise = promiseCache[flagKey];
			if (existingPromise) {
				content.eppo.assignment = await existingPromise;
				return;
			}

			const attributes: Record<string, string> = {};

			if (content.url_attribute) {
				const attr = url.searchParams.get(content.url_attribute);
				if (attr !== null) {
					attributes[content.url_attribute] = attr;
				}
			}

			const assignmentPromise = getAssignment(serverFetch, flagKey, {
				defaultValue: `${content.eppo.feature_flag.key}_control`,
				subject: content.subject,
				...attributes
			});

			promiseCache[flagKey] = assignmentPromise;

			try {
				const assignment = await assignmentPromise;
				content.eppo.assignment = assignment;
				cache[flagKey] = assignment;
			} catch (error) {
				console.error(`Error fetching Eppo assignment for ${flagKey}:`, error);
				const fallback = { variation: `${flagKey}_control`, action: null };
				content.eppo.assignment = fallback;
				cache[flagKey] = fallback;
			}
		}
	};
}
